<script setup lang="ts">
import { ref, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import type { MenuItemVariant } from '@/types/menu';

interface Props {
  show: boolean;
  variant?: MenuItemVariant;
  editingIndex?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  variant: undefined,
  editingIndex: null
});

const emit = defineEmits<{
  'update:show': [value: boolean];
  'save': [variant: Omit<MenuItemVariant, 'id'>];
  'cancel': [];
}>();

const { t } = useI18n();

type VariantFormState = {
  name: string;
  price: number;
  discount_price?: number;
  is_available: boolean;
};

const variantForm = ref<VariantFormState>({
  name: '',
  price: 0,
  discount_price: undefined,
  is_available: true
});

const resetForm = () => {
  variantForm.value = {
    name: '',
    price: 0,
    discount_price: undefined,
    is_available: true
  };
};

// Watch for variant changes to populate form
watch(() => props.variant, (newVariant) => {
  if (newVariant) {
    variantForm.value = {
      name: newVariant.name,
      price: typeof newVariant.price === 'string' ? parseFloat(newVariant.price) : newVariant.price,
      discount_price: newVariant.discount_price,
      is_available: newVariant.is_available ?? true
    };
  } else {
    resetForm();
  }
}, { immediate: true });

const handleSave = () => {
  if (!variantForm.value.name || variantForm.value.price === null || variantForm.value.price === undefined || variantForm.value.price < 0) {
    return;
  }

  const variantData = {
    name: variantForm.value.name,
    price: Number(variantForm.value.price),
    discount_price: variantForm.value.discount_price ? Number(variantForm.value.discount_price) : undefined,
    is_available: variantForm.value.is_available
  };

  emit('save', variantData);
  resetForm();
  emit('update:show', false);
};

const handleCancel = () => {
  resetForm();
  emit('cancel');
  emit('update:show', false);
};
</script>

<template>
  <div
    v-if="show"
    class="mt-4 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-4"
  >
    <div class="flex items-center justify-between mb-4">
      <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100">
        {{ editingIndex !== null ? t('app.forms.variant.edit') : t('app.forms.variant.add') }}
      </h4>
      <button
        type="button"
        class="rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
        @click="handleCancel"
      >
        <XMarkIcon
          class="h-5 w-5"
          aria-hidden="true"
        />
      </button>
    </div>

    <div class="space-y-4">
      <!-- Variant name -->
      <div>
        <label
          for="variant-name-inline"
          class="block text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {{ t('app.forms.variant.name') }}
        </label>
        <input
          id="variant-name-inline"
          v-model="variantForm.name"
          type="text"
          class="mt-1 p-2 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-800 dark:text-gray-100"
          placeholder="e.g., Small, Large, Iced"
        >
      </div>

      <!-- Variant price -->
      <div>
        <label
          for="variant-price-inline"
          class="block text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {{ t('app.forms.variant.price') }}
        </label>
        <div class="mt-1 relative rounded-md shadow-sm">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span class="text-gray-500 sm:text-sm">
              {{ t('app.forms.price_symbol') }}
            </span>
          </div>
          <input
            id="variant-price-inline"
            v-model.number="variantForm.price"
            type="number"
            step="0.01"
            min="0"
            class="block w-full pl-7 pr-12 py-2 rounded-md border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-gray-100 sm:text-sm"
            :placeholder="t('app.forms.placeholder_price')"
          >
        </div>
      </div>

      <!-- Variant discount price -->
      <div>
        <label
          for="variant-discount-price-inline"
          class="block text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {{ t('app.forms.variant.discount_price') }} ({{ t('app.forms.discount_placeholder').split(' ')[0] }})
        </label>
        <div class="mt-1 relative rounded-md shadow-sm">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span class="text-gray-500 sm:text-sm">
              {{ t('app.forms.price_symbol') }}
            </span>
          </div>
          <input
            id="variant-discount-price-inline"
            v-model.number="variantForm.discount_price"
            type="number"
            step="0.01"
            min="0"
            class="block w-full pl-7 pr-12 py-2 rounded-md border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-gray-100 sm:text-sm"
            :placeholder="t('app.forms.discount_placeholder')"
          >
        </div>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {{ t('app.forms.discount_price_help') }}
        </p>
      </div>

      <!-- Available toggle -->
      <div class="flex items-center">
        <input
          id="variant-available-inline"
          v-model="variantForm.is_available"
          type="checkbox"
          class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
        >
        <label
          for="variant-available-inline"
          class="ml-2 block text-sm text-gray-700 dark:text-gray-200"
        >
          {{ t('app.forms.variant.available') }}
        </label>
      </div>

      <!-- Save/Cancel buttons -->
      <div class="flex justify-end gap-2 pt-2">
        <button
          type="button"
          class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
          @click="handleCancel"
        >
          {{ t('app.actions.cancel') }}
        </button>
        <button
          type="button"
          class="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          @click="handleSave"
        >
          {{ editingIndex !== null ? t('app.actions.update') : t('app.actions.save') }}
        </button>
      </div>
    </div>
  </div>
</template>
